/**
 * Finds which caption tracks an MCC file actually carries, so the Extract Subtitles
 * modal only offers (and only decodes) tracks with captions in them:
 * - CEA-608 CC1-CC4: field 1 holds CC1/CC2 and field 2 CC3/CC4. A control code's
 *   channel bit says which channel the following characters belong to.
 * - CEA-708 services 1-63: DTVCC packets are reassembled from the cc_data and each
 *   service block's header names its service.
 *
 * Plain JavaScript with no imports, so it also runs under Node (ccc-tests).
 */

const TC_LINE = /^(\d\d:\d\d:\d\d[:;.,]\d\d)\t(.+)$/;

/* MCC short forms (SMPTE ST 334 / MacCaption): a letter stands for a byte run. */
const SHORT_FORMS = {
    G: "FA0000",
    H: "FA0000".repeat(2),
    I: "FA0000".repeat(3),
    J: "FA0000".repeat(4),
    K: "FA0000".repeat(5),
    L: "FA0000".repeat(6),
    M: "FA0000".repeat(7),
    N: "FA0000".repeat(8),
    O: "FA0000".repeat(9),
    P: "FB8080",
    Q: "FC8080",
    R: "FD8080",
    S: "9669",
    T: "6101",
    U: "E1000000",
    Z: "00",
};

function expand(data) {
    let hex = "";
    for (const ch of data) {
        hex += SHORT_FORMS[ch] ?? ch;
    }
    return hex;
}

function toBytes(hex) {
    const bytes = new Uint8Array(hex.length >> 1);
    for (let i = 0; i < bytes.length; i++) {
        bytes[i] = parseInt(hex.substr(i * 2, 2), 16);
    }
    return bytes;
}

/* The cc_data triplets of one ANC packet (DID 61, SDID 01, DC, then the CDP). */
function ccTriplets(bytes) {
    // 61 01 DC 96 69 cdp_length frame_rate flags seq seq [71 tc tc tc tc] 72 count ...
    let at = bytes[0] === 0x61 && bytes[1] === 0x01 ? 3 : 0;
    if (bytes[at] !== 0x96 || bytes[at + 1] !== 0x69) return [];
    const flags = bytes[at + 4];
    at += 7;
    if (flags & 0x80) {
        if (bytes[at] !== 0x71) return [];
        at += 5;
    }
    if (!(flags & 0x40) || bytes[at] !== 0x72) return [];
    const count = bytes[at + 1] & 0x1f;
    at += 2;
    const triplets = [];
    for (let i = 0; i < count && at + 3 <= bytes.length; i++, at += 3) {
        triplets.push([bytes[at], bytes[at + 1], bytes[at + 2]]);
    }
    return triplets;
}

/* A 608 byte pair's channel bit and whether it carries text. */
function scan608Pair(state, b1, b2, counts, field) {
    const c1 = b1 & 0x7f;
    const c2 = b2 & 0x7f;
    if (c1 === 0 && c2 === 0) return;
    if (c1 >= 0x10 && c1 <= 0x1f) {
        // Control code (or special/extended character): bit 3 selects the second channel.
        state.channel = c1 & 0x08 ? 1 : 0;
        state.data = true;
        // Special and extended characters are text too.
        if ((c1 & 0x77) === 0x11 && c2 >= 0x30 && c2 <= 0x3f) counts[state.channel] += 1;
        if ((c1 & 0x76) === 0x12 && c2 >= 0x20 && c2 <= 0x3f) counts[state.channel] += 1;
        return;
    }
    if (c1 >= 0x01 && c1 <= 0x0f) {
        // XDS (field 2 only): not captions.
        if (field === 2) state.data = false;
        return;
    }
    if (c1 >= 0x20 && state.data) {
        counts[state.channel] += c2 >= 0x20 ? 2 : 1;
    }
}

/* Service numbers in one DTVCC packet (header byte first). */
function scanDtvccPacket(packet, services) {
    const sizeCode = packet[0] & 0x3f;
    const size = sizeCode === 0 ? 128 : sizeCode * 2;
    let at = 1;
    const end = Math.min(size, packet.length);
    while (at < end) {
        let service = packet[at] >> 5;
        const blockSize = packet[at] & 0x1f;
        at += 1;
        if (service === 7 && blockSize !== 0) {
            service = packet[at] & 0x3f;
            at += 1;
        }
        if (service === 0 || blockSize === 0) break; // null block: padding to the end
        // A block of nothing but NUL/ETX padding carries no captions.
        const block = packet.subarray(at, at + blockSize);
        if (block.some((b) => b !== 0x00 && b !== 0x03)) {
            services.set(service, (services.get(service) || 0) + blockSize);
        }
        at += blockSize;
    }
}

/**
 * @param {string} mccText the MCC file
 * @returns {{cea608: {channel: string, characters: number}[], cea708: {service: number, bytes: number}[]}}
 *   cea608 lists CC1-CC4 with any text; cea708 lists the services with data, lowest first.
 */
export function scanMccTracks(mccText) {
    const counts608 = { 1: [0, 0], 2: [0, 0] };
    const state608 = { 1: { channel: 0, data: true }, 2: { channel: 0, data: true } };
    const services = new Map();
    let packet = null;

    for (const line of mccText.split(/\r?\n/)) {
        const match = TC_LINE.exec(line);
        if (!match) continue;
        for (const [header, b1, b2] of ccTriplets(toBytes(expand(match[2].trim())))) {
            if ((header & 0xf8) !== 0xf8 || !(header & 0x04)) continue; // markers, cc_valid
            const type = header & 0x03;
            if (type === 0 || type === 1) {
                const field = type + 1;
                scan608Pair(state608[field], b1, b2, counts608[field], field);
            } else if (type === 3) {
                if (packet) scanDtvccPacket(Uint8Array.from(packet), services);
                packet = [b1, b2];
            } else if (packet) {
                packet.push(b1, b2);
            }
        }
    }
    if (packet) scanDtvccPacket(Uint8Array.from(packet), services);

    const cea608 = [
        ["CC1", counts608[1][0]],
        ["CC2", counts608[1][1]],
        ["CC3", counts608[2][0]],
        ["CC4", counts608[2][1]],
    ]
        .filter(([, characters]) => characters > 0)
        .map(([channel, characters]) => ({ channel, characters }));
    const cea708 = [...services.entries()]
        .sort((a, b) => a[0] - b[0])
        .map(([service, bytes]) => ({ service, bytes }));
    return { cea608, cea708 };
}
